import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X, Download, Play } from "lucide-react";

const TOTAL = 83; // number of slides in the deck
const PDF_URL = "/pinkwater-deck/pinkwater-overview.pdf";
const slideSrc = (n) => `/pinkwater-deck/slide-${n}.jpg`;
const thumbSrc = (n) => `/pinkwater-deck/thumb-${n}.jpg`;

export default function PinkwaterDeck() {
  const [index, setIndex] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);

  const go = useCallback(
    (n) => setIndex(Math.min(TOTAL, Math.max(1, n))),
    []
  );
  const next = useCallback(() => setIndex((i) => (i >= TOTAL ? 1 : i + 1)), []);
  const prev = useCallback(() => setIndex((i) => (i <= 1 ? TOTAL : i - 1)), []);

  // keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // lock body scroll in fullscreen
  useEffect(() => {
    document.body.style.overflow = fullscreen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [fullscreen]);

  return (
    <section className="py-20 msv-dot-bg">
      <div className="mx-auto max-w-[1100px] px-6">
        <div className="text-center mb-10">
          <p className="msv-eyebrow mb-4">Presentation</p>
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
            Pinkwater® Overview Deck
          </h2>
          <p className="text-[#6a6a6a] leading-relaxed mt-5 max-w-2xl mx-auto">
            Explore our full Pinkwater® presentation. Use the arrows, thumbnails, or your keyboard
            to navigate all {TOTAL} slides — or download the complete PDF.
          </p>
        </div>

        {/* Viewer */}
        <div className="msv-card overflow-hidden">
          <div className="relative bg-[#0f1b2d] group">
            <img
              src={slideSrc(index)}
              alt={`Pinkwater slide ${index}`}
              className="w-full aspect-video object-contain bg-[#0f1b2d]"
            />
            {/* preload neighbors */}
            <img src={slideSrc(index === TOTAL ? 1 : index + 1)} alt="" className="hidden" />

            {/* arrows */}
            <button
              onClick={prev}
              aria-label="Previous slide"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-[#d81b76] text-white flex items-center justify-center transition-colors backdrop-blur-sm"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={next}
              aria-label="Next slide"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-[#d81b76] text-white flex items-center justify-center transition-colors backdrop-blur-sm"
            >
              <ChevronRight size={24} />
            </button>

            {/* fullscreen + counter */}
            <button
              onClick={() => setFullscreen(true)}
              aria-label="View fullscreen"
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
            >
              <Maximize2 size={18} />
            </button>
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/55 text-white text-xs font-heading tracking-wider px-3 py-1.5 rounded-full backdrop-blur-sm">
              {index} / {TOTAL}
            </span>
          </div>

          {/* controls bar */}
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-gray-100 bg-white">
            <div className="flex items-center gap-2 text-sm text-[#6a6a6a]">
              <Play size={15} className="text-[#d81b76]" />
              <span className="font-heading uppercase tracking-[0.08em] text-xs">
                Slide {index} of {TOTAL}
              </span>
            </div>
            <a
              href={PDF_URL}
              download
              className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.12em] text-xs text-white bg-[#d81b76] px-4 py-2.5 rounded-lg hover:bg-[#b5155f] transition-colors"
            >
              <Download size={15} /> Download PDF
            </a>
          </div>
        </div>

        {/* Thumbnails */}
        <div className="mt-5 flex gap-2 overflow-x-auto pb-3 msv-scroll">
          {Array.from({ length: TOTAL }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              onClick={() => go(n)}
              aria-label={`Go to slide ${n}`}
              className={`shrink-0 w-28 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                n === index
                  ? "border-[#d81b76] shadow-md"
                  : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img
                src={thumbSrc(n)}
                alt={`Slide ${n} thumbnail`}
                loading="lazy"
                className="w-full aspect-video object-cover bg-[#0f1b2d]"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Fullscreen lightbox */}
      {fullscreen && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col animate-fade-up">
          <div className="flex items-center justify-between px-6 py-4">
            <span className="text-white/80 font-heading uppercase tracking-[0.12em] text-sm">
              Pinkwater® Overview — {index} / {TOTAL}
            </span>
            <div className="flex items-center gap-3">
              <a
                href={PDF_URL}
                download
                className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm"
              >
                <Download size={18} />
              </a>
              <button
                onClick={() => setFullscreen(false)}
                aria-label="Close fullscreen"
                className="text-white/80 hover:text-white"
              >
                <X size={26} />
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 md:px-16 relative">
            <button
              onClick={prev}
              aria-label="Previous slide"
              className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-[#d81b76] text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={28} />
            </button>
            <img
              src={slideSrc(index)}
              alt={`Pinkwater slide ${index}`}
              className="max-h-[80vh] max-w-full object-contain"
            />
            <button
              onClick={next}
              aria-label="Next slide"
              className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-[#d81b76] text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight size={28} />
            </button>
          </div>
          <div className="text-center pb-6">
            <span className="text-white/50 text-xs">Use ← → arrow keys to navigate • Esc to close</span>
          </div>
        </div>
      )}
    </section>
  );
}
